import React, { useEffect, useState } from "react";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const ENDPOINT = process.env.REACT_APP_BLOGS_ENDPOINT;

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean'],
    ],
};

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "de", label: "German" },
];

const emptyForm = { Title: "", ShortDescription: "", Content: "", ThumbnailFileName: "", LanguageCode: "en" };

export default function Admin() {
    const [apiKey, setApiKey] = useState(() => sessionStorage.getItem("admin_api_key") || "");
    const [apiKeyInput, setApiKeyInput] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [blogs, setBlogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const [mode, setMode] = useState("list"); // "list" | "create" | "edit"
    const [editingBlog, setEditingBlog] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
    const [thumbnailUploadError, setThumbnailUploadError] = useState("");

    const [languageFilter, setLanguageFilter] = useState("");

    // Validate stored key on mount by trying to fetch blogs
    useEffect(() => {
        if (apiKey) {
            fetchBlogs(apiKey, languageFilter);
        }
    }, []);

    // Re-fetch when language filter changes (only when authenticated)
    useEffect(() => {
        if (isAuthenticated) {
            fetchBlogs(apiKey, languageFilter);
        }
    }, [languageFilter]);

    async function fetchBlogs(key, langCode = "") {
        setIsLoading(true);
        setFetchError("");
        try {
            const url = langCode ? `${ENDPOINT}?languageCode=${encodeURIComponent(langCode)}` : ENDPOINT;
            const res = await fetch(url);
            if (!res.ok) throw new Error("Failed to load blogs");
            const data = await res.json();
            setBlogs(data);
            setIsAuthenticated(true);
        } catch (err) {
            setFetchError(err.message);
        } finally {
            setIsLoading(false);
        }
    }

    function handleKeySubmit(e) {
        e.preventDefault();
        const trimmed = apiKeyInput.trim();
        if (!trimmed) return;
        sessionStorage.setItem("admin_api_key", trimmed);
        setApiKey(trimmed);
        fetchBlogs(trimmed);
    }

    function handleLogout() {
        sessionStorage.removeItem("admin_api_key");
        setApiKey("");
        setApiKeyInput("");
        setIsAuthenticated(false);
        setBlogs([]);
        setMode("list");
    }

    function openCreate() {
        setFormData(emptyForm);
        setFormError("");
        setFormSuccess("");
        setEditingBlog(null);
        setMode("create");
    }

    function openEdit(blog) {
        setFormData({
            Title: blog.Title || "",
            ShortDescription: blog.ShortDescription || "",
            Content: blog.Content || "",
            ThumbnailFileName: blog.ThumbnailFileName || "",
            LanguageCode: blog.LanguageCode || "en",
        });
        setFormError("");
        setFormSuccess("");
        setEditingBlog(blog);
        setMode("edit");
    }

    function handleFormChange(e) {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    async function handleThumbnailUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        setThumbnailUploadError("");
        setIsUploadingThumbnail(true);
        try {
            const body = new FormData();
            body.append("file", file);
            const res = await fetch(`${ENDPOINT}/upload-image`, {
                method: "POST",
                headers: { "X-API-Key": apiKey },
                body,
            });
            if (res.status === 401) throw new Error("Invalid API key.");
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Upload failed.");
            }
            const { fileName } = await res.json();
            setFormData(prev => ({ ...prev, ThumbnailFileName: fileName }));
        } catch (err) {
            setThumbnailUploadError(err.message);
        } finally {
            setIsUploadingThumbnail(false);
            e.target.value = "";
        }
    }

    async function handleThumbnailRemove() {
        const fileName = formData.ThumbnailFileName;
        setFormData(prev => ({ ...prev, ThumbnailFileName: "" }));
        if (!fileName) return;
        try {
            await fetch(`${ENDPOINT}/delete-image?fileName=${encodeURIComponent(fileName)}`, {
                method: "DELETE",
                headers: { "X-API-Key": apiKey },
            });
        } catch {
            // best-effort delete; form already cleared
        }
    }

    async function handleCreate(e) {
        e.preventDefault();
        setFormError("");
        setFormSuccess("");
        setIsSubmitting(true);
        try {
            const res = await fetch(ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-API-Key": apiKey,
                },
                body: JSON.stringify(formData),
            });
            if (res.status === 401) throw new Error("Invalid API key.");
            if (!res.ok) throw new Error("Failed to create blog.");
            const created = await res.json();
            setBlogs(prev => [created, ...prev]);
            setFormSuccess("Blog created successfully.");
            setFormData(emptyForm);
        } catch (err) {
            setFormError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleUpdate(e) {
        e.preventDefault();
        setFormError("");
        setFormSuccess("");
        setIsSubmitting(true);
        try {
            const res = await fetch(`${ENDPOINT}/${editingBlog.ID}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-API-Key": apiKey,
                },
                body: JSON.stringify(formData),
            });
            if (res.status === 401) throw new Error("Invalid API key.");
            if (res.status === 404) throw new Error("Blog not found.");
            if (!res.ok) throw new Error("Failed to update blog.");
            const updated = await res.json();
            setBlogs(prev => prev.map(b => b.ID === updated.ID ? updated : b));
            setFormSuccess("Blog updated successfully.");
        } catch (err) {
            setFormError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleDelete(id) {
        setIsDeleting(true);
        try {
            const res = await fetch(`${ENDPOINT}/${id}`, {
                method: "DELETE",
                headers: { "X-API-Key": apiKey },
            });
            if (res.status === 401) throw new Error("Invalid API key.");
            if (res.status === 404) throw new Error("Blog not found.");
            if (!res.ok) throw new Error("Failed to delete blog.");
            setBlogs(prev => prev.filter(b => b.ID !== id));
            setDeleteConfirmId(null);
        } catch (err) {
            setFetchError(err.message);
        } finally {
            setIsDeleting(false);
        }
    }

    // ── Key entry screen ──────────────────────────────────────────────────────
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
                    <img
                        src={require("../media/images/logo.png")}
                        alt="Eloquenti"
                        className="w-32 mx-auto mb-6"
                    />
                    <h1 className="text-2xl font-semibold text-gray-800 text-center mb-6">
                        Blog Admin
                    </h1>
                    <form onSubmit={handleKeySubmit} className="flex flex-col gap-4">
                        <input
                            type="password"
                            placeholder="API Key"
                            value={apiKeyInput}
                            onChange={e => setApiKeyInput(e.target.value)}
                            className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B29FF]"
                            required
                        />
                        {fetchError && (
                            <p className="text-red-600 text-sm">{fetchError}</p>
                        )}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-[#9B29FF] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#7a1fd4] transition-colors disabled:opacity-50"
                        >
                            {isLoading ? "Signing in..." : "Sign in"}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ── Create / Edit form ────────────────────────────────────────────────────
    if (mode === "create" || mode === "edit") {
        const isEdit = mode === "edit";
        return (
            <div className="min-h-screen bg-gray-100 px-4 py-8">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => setMode("list")}
                            className="text-sm text-[#9B29FF] hover:underline"
                        >
                            ← Back to blogs
                        </button>
                        <button
                            onClick={handleLogout}
                            className="text-sm text-gray-500 hover:text-gray-700"
                        >
                            Sign out
                        </button>
                    </div>

                    <div className="bg-white rounded-2xl shadow p-8">
                        <h2 className="text-xl font-semibold text-gray-800 mb-6">
                            {isEdit ? `Edit: ${editingBlog.Title}` : "New Blog Post"}
                        </h2>

                        <form onSubmit={isEdit ? handleUpdate : handleCreate} className="flex flex-col gap-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    name="Title"
                                    value={formData.Title}
                                    onChange={handleFormChange}
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B29FF]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                                <textarea
                                    name="ShortDescription"
                                    value={formData.ShortDescription}
                                    onChange={handleFormChange}
                                    rows={3}
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B29FF] resize-vertical"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                                <select
                                    name="LanguageCode"
                                    value={formData.LanguageCode}
                                    onChange={handleFormChange}
                                    className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B29FF]"
                                >
                                    {LANGUAGES.map(l => (
                                        <option key={l.code} value={l.code}>{l.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.Content}
                                    onChange={(value) => setFormData(prev => ({ ...prev, Content: value }))}
                                    modules={QUILL_MODULES}
                                    className="bg-white rounded-lg"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>
                                {formData.ThumbnailFileName ? (
                                    <div className="flex items-start gap-4">
                                        <img
                                            src={`${process.env.REACT_APP_BLOGS_MEDIA_URL}/${formData.ThumbnailFileName}`}
                                            alt="Thumbnail preview"
                                            className="w-48 h-32 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                                            onError={e => { e.target.style.display = 'none'; }}
                                        />
                                        <div className="flex flex-col gap-2 pt-1">
                                            <p className="text-xs text-gray-500 break-all">{formData.ThumbnailFileName}</p>
                                            <label className="cursor-pointer text-xs border border-gray-300 text-gray-700 rounded px-3 py-1 hover:bg-gray-50 transition-colors text-center">
                                                {isUploadingThumbnail ? "Uploading..." : "Replace"}
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                                    className="hidden"
                                                    onChange={handleThumbnailUpload}
                                                    disabled={isUploadingThumbnail}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                onClick={handleThumbnailRemove}
                                                className="text-xs border border-red-200 text-red-600 rounded px-3 py-1 hover:bg-red-50 transition-colors"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isUploadingThumbnail ? "border-gray-200 bg-gray-50" : "border-gray-300 hover:border-[#9B29FF] hover:bg-purple-50"}`}>
                                        {isUploadingThumbnail ? (
                                            <span className="text-sm text-gray-400">Uploading...</span>
                                        ) : (
                                            <>
                                                <span className="text-sm text-gray-500">Click to upload thumbnail</span>
                                                <span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP</span>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            className="hidden"
                                            onChange={handleThumbnailUpload}
                                            disabled={isUploadingThumbnail}
                                        />
                                    </label>
                                )}
                                {thumbnailUploadError && (
                                    <p className="text-red-600 text-xs mt-1">{thumbnailUploadError}</p>
                                )}
                            </div>

                            {formError && (
                                <p className="text-red-600 text-sm bg-red-50 rounded-lg px-4 py-2">{formError}</p>
                            )}
                            {formSuccess && (
                                <p className="text-green-600 text-sm bg-green-50 rounded-lg px-4 py-2">{formSuccess}</p>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-[#9B29FF] text-white rounded-lg px-6 py-2 text-sm font-medium hover:bg-[#7a1fd4] transition-colors disabled:opacity-50"
                                >
                                    {isSubmitting
                                        ? (isEdit ? "Saving..." : "Creating...")
                                        : (isEdit ? "Save changes" : "Create blog")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode("list")}
                                    className="border border-gray-300 text-gray-700 rounded-lg px-6 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ── Blog list ─────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-100 px-4 py-8">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <img src={require("../media/images/logo.png")} alt="Eloquenti" className="w-24" />
                        <h1 className="text-xl font-semibold text-gray-800">Blog Admin</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={languageFilter}
                            onChange={e => setLanguageFilter(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B29FF]"
                        >
                            <option value="">All languages</option>
                            {LANGUAGES.map(l => (
                                <option key={l.code} value={l.code}>{l.label}</option>
                            ))}
                        </select>
                        <button
                            onClick={openCreate}
                            className="bg-[#9B29FF] text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-[#7a1fd4] transition-colors"
                        >
                            + New post
                        </button>
                        <button
                            onClick={handleLogout}
                            className="border border-gray-300 text-gray-600 rounded-lg px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                        >
                            Sign out
                        </button>
                    </div>
                </div>

                {fetchError && (
                    <p className="text-red-600 text-sm bg-red-50 rounded-lg px-4 py-3 mb-4">{fetchError}</p>
                )}

                {isLoading ? (
                    <p className="text-gray-500 text-sm">Loading blogs...</p>
                ) : blogs.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow p-12 text-center">
                        <p className="text-gray-500 mb-4">No blog posts yet.</p>
                        <button
                            onClick={openCreate}
                            className="bg-[#9B29FF] text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-[#7a1fd4] transition-colors"
                        >
                            Create your first post
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50">
                                    <th className="text-left px-6 py-3 font-medium text-gray-600">Title</th>
                                    <th className="text-left px-6 py-3 font-medium text-gray-600 hidden md:table-cell">Short Description</th>
                                    <th className="text-left px-6 py-3 font-medium text-gray-600 hidden sm:table-cell">Lang</th>
                                    <th className="text-left px-6 py-3 font-medium text-gray-600 hidden lg:table-cell">Created</th>
                                    <th className="px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {blogs.map(blog => (
                                    <tr key={blog.ID} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {blog.ThumbnailFileName && (
                                                    <img
                                                        src={`${process.env.REACT_APP_BLOGS_MEDIA_URL}/${blog.ThumbnailFileName}`}
                                                        alt=""
                                                        className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                                                        onError={e => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span className="font-medium text-gray-800 line-clamp-1">{blog.Title}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 hidden md:table-cell max-w-xs">
                                            <span className="line-clamp-2">{blog.ShortDescription}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden sm:table-cell">
                                            <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700 uppercase">
                                                {blog.LanguageCode || "en"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-400 whitespace-nowrap hidden lg:table-cell">
                                            {blog.CreatedDate
                                                ? new Date(blog.CreatedDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                                                : "—"}
                                        </td>
                                        <td className="px-6 py-4">
                                            {deleteConfirmId === blog.ID ? (
                                                <div className="flex items-center gap-2 justify-end">
                                                    <span className="text-gray-600 text-xs">Delete?</span>
                                                    <button
                                                        onClick={() => handleDelete(blog.ID)}
                                                        disabled={isDeleting}
                                                        className="text-xs bg-red-600 text-white rounded px-3 py-1 hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                        {isDeleting ? "..." : "Yes"}
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirmId(null)}
                                                        className="text-xs border border-gray-300 text-gray-600 rounded px-3 py-1 hover:bg-gray-100"
                                                    >
                                                        No
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 justify-end">
                                                    <button
                                                        onClick={() => openEdit(blog)}
                                                        className="text-xs border border-gray-300 text-gray-700 rounded px-3 py-1 hover:bg-gray-100 transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirmId(blog.ID)}
                                                        className="text-xs border border-red-200 text-red-600 rounded px-3 py-1 hover:bg-red-50 transition-colors"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
